import CardWithTitle from "features/affiliation/common/CardWithTitle";
import { AffiliationTabs } from "features/affiliation/common/types";
import PieChart from "common/Charts/PieChart/PieChart";
import {
  EARNINGS_PIE_CHART_KEY_TO_HELPER_TOOLTIP,
  EARNINGS_PIE_CHART_KEY_TO_VALUE_COLOR,
} from "features/affiliation/common/constants";
import { CSSProperties, useMemo } from "react";
import { useTheme } from "styled-components";
import Text from "common/Typography/Text";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import { getUsdValueWithPrecision } from "features/affiliation/common/utils";
import { typography } from "@shared/common/Themes/typography";
import Flex from "common/Boxes/Flex";
import { useEarningsPieChart } from "features/affiliation/common/hooks";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";

interface EarningsPieChartProps {
  horizontal?: boolean;
  hideTitle?: boolean;
  inOverview?: boolean;
}

const EarningsPieChart = ({
  horizontal,
  hideTitle,
  inOverview,
}: EarningsPieChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const appTheme = useTheme();
  const {
    commissions,
    sumCommissions,
    commissionHistoryQuery,
    earningsPreprocessPieChart,
  } = useEarningsPieChart();
  const isLoading = commissionHistoryQuery.isLoading;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const pieChartData = useMemo(
    () =>
      earningsPreprocessPieChart.map((dataPoint) => ({
        ...dataPoint,
        legendDisplay: (
          <Flex
            $alignItems="baseline"
            $justifyContent="flex-start"
            style={{ flexWrap: "wrap" }}
          >
            <Text $size={typography.fontSizes.S}>
              {t(
                dataPoint.name as TranslationKeys<LanguageNameSpaces.AFFILIATION>
              )}
            </Text>
            <HelperTooltip
              title={t(
                dataPoint.name as TranslationKeys<LanguageNameSpaces.AFFILIATION>
              )}
              description={t(
                EARNINGS_PIE_CHART_KEY_TO_HELPER_TOOLTIP[dataPoint.name]
              )}
              analyticsPageName={AnalyticsPageName.AFFILIATION}
              analyticsFieldName={dataPoint.name}
              containerStyle={{ margin: "0 4px" }}
              iconStyle={{ color: appTheme.colors.primaryGrey }}
            />
            <Text style={{ marginRight: 6 }}>:</Text>
            <Text
              color={
                appTheme.colors[
                  EARNINGS_PIE_CHART_KEY_TO_VALUE_COLOR[dataPoint.name]
                ]
              }
            >
              {getUsdValueWithPrecision(dataPoint.originalValue)}
            </Text>
          </Flex>
        ),
      })),
    [earningsPreprocessPieChart]
  );

  let cardProps;
  if (!hideTitle) {
    cardProps = {
      title: t("earnings.title"),
      relatedTab: AffiliationTabs.EARNINGS,
    };
  }
  if (!inOverview) {
    cardProps = {
      ...cardProps,
      cardStyle: {
        height: "50%",
        maxHeight: 350,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    };
  }

  let chartStyle: CSSProperties = { flex: "none" };
  if (isBottomNavigator) {
    chartStyle = { ...chartStyle, marginRight: 12 };
  }

  const memoizedPieChart = useMemo(
    () => (
      <PieChart
        data={pieChartData}
        loading={isLoading}
        total={getUsdValueWithPrecision(sumCommissions())}
        numGridItems={2}
        chartParentElementId="earnings-chart"
        horizontal={horizontal}
        chartSize={isBottomNavigator ? 160 : 200}
        chartStyle={chartStyle}
      />
    ),
    [pieChartData, isLoading, commissions, horizontal]
  );

  return (
    <CardWithTitle {...cardProps}>
      <div>{memoizedPieChart}</div>
    </CardWithTitle>
  );
};

export default EarningsPieChart;
